"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaLock, FaMapMarkerAlt, FaStore, FaTruck } from "react-icons/fa";
import { FiActivity, FiCalendar, FiEdit2, FiGift, FiPackage, FiRepeat, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import { useParams } from "next/navigation";
import { PaymentMethodPicker } from "@/components/payment-method-picker";
import { RainforestPayinSessionProvider } from "@/components/rainforest/rainforest-payin-session-context";
import { Button } from "@/components/ui";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { usePurchaseAuth } from "@/contexts/purchase-auth-provider";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { buildShopCheckoutIdempotencyKey } from "@/lib/checkout/checkout-idempotency";
import { formatCurrency } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { ItemType } from "@/types/enums/item-type";
import { MembershipShopBillingStart } from "@/types/enums/membership";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { GiftCardRecipientInput, GiftCardShopCartLine, MembershipShopCartLine, PackageShopCartLine, PurchaseBuyer, PurchaseCharges, RegularCatalogShopCartLine, ShopCartLine, ShopPurchasePricingResponse } from "@/types/shop";
import { getAdapter } from "./adapters";
import { ShopCartLinePricing, cartCanShip, getShopCartPricingPreview, quantityMaxFor, withLineQuantity } from "./cart-line-utils";
import { PatientCreditPaymentOption } from "./components/patient-credit-payment-option";
import PromotionProgress from "./components/promotion-progress";
import GiftCardConfiguratorDialog from "./gift-cards/configurator-dialog";
import { formatScheduledSendAt } from "./gift-cards/utils";
import { isMembershipBillingStartFuture, membershipBillingStartLabel } from "./memberships/utils";
import { getShopPromotionMessages } from "./promotion-messaging";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { chargePurchaseWithSavedMethod, completePurchaseWithPatientCredit, createMembershipPaymentMethodConfig, fetchPurchasePricing, saveMembershipPaymentMethod } from "@/api/shop/shop";
import { shopMutationOptions } from "@/query/shop/shop-query";
import { tenantQueryOptions } from "@/query/tenant";
const recipientDisplayName = (recipient: GiftCardRecipientInput): string => {
  const name = [recipient.firstName, recipient.lastName].map(part => part?.trim()).filter(Boolean).join(" ");
  return name || recipient.email?.trim() || recipient.phone?.trim() || "Recipient";
};
const giftCardSubtitle = (line: GiftCardShopCartLine): string => {
  const keepCount = line.quantity - line.recipients.length;
  const firstRecipient = line.recipients[0];
  if (firstRecipient) {
    const scheduled = formatScheduledSendAt(firstRecipient.scheduledSendAt);
    const suffix = line.recipients.length > 1 ? ` +${line.recipients.length - 1} more` : "";
    return `${recipientDisplayName(firstRecipient)}${suffix} · ${scheduled ? `Send on ${scheduled}` : "Send immediately"}`;
  }
  if (keepCount > 0) {
    return keepCount === 1 ? "Keeping for yourself" : `Keeping ${keepCount} for yourself`;
  }
  return "Digital gift card";
};
const packageSubtitle = (line: PackageShopCartLine): string => {
  const savings = line.snapshot.packageSavings ?? 0;
  if (savings > 0) {
    return `Includes ${formatCurrency(line.unitPriceCents + savings)} in value`;
  }
  return line.snapshot.description || "Added to your account after payment";
};
const membershipSubtitle = (line: MembershipShopCartLine): string => {
  const formCopy = line.form?.completed === false ? ` · ${line.form.name} required` : "";
  return `${membershipBillingStartLabel(line.snapshot.membership?.shopBillingStart)}${formCopy}`;
};
const regularCatalogSubtitle = (line: RegularCatalogShopCartLine): string => {
  if (line.snapshot.category) {
    return line.snapshot.category;
  }
  switch (line.itemType) {
    case ItemType.SERVICE:
      return "Prepaid service";
    case ItemType.PRODUCT:
      return "Product";
    case ItemType.MEDICATION:
      return "Medication";
    default:
      return "Shop item";
  }
};
const checkoutLineIcon = (line: ShopCartLine) => {
  switch (line.itemType) {
    case ItemType.GIFT_CARD:
      return FiGift;
    case ItemType.PACKAGE:
      return FiPackage;
    case ItemType.MEMBERSHIP:
      return FiRepeat;
    case ItemType.SERVICE:
      return FiCalendar;
    case ItemType.PRODUCT:
      return FiShoppingBag;
    case ItemType.MEDICATION:
      return FiActivity;
    default:
      return FiPackage;
  }
};
interface CheckoutLineItemProps {
  line: ShopCartLine;
  onReplace: (line: ShopCartLine) => void;
  onRemove: () => void;
  pricing: ShopCartLinePricing;
}
const CheckoutLineItem: React.FC<CheckoutLineItemProps> = t0 => {
  const $ = _c(79);
  const {
    line,
    onReplace,
    onRemove,
    pricing
  } = t0;
  const [isEditing, setIsEditing] = useState(false);
  const isGiftCard = line.itemType === ItemType.GIFT_CARD;
  const isPackage = line.itemType === ItemType.PACKAGE;
  const isMembership = line.itemType === ItemType.MEMBERSHIP;
  let t1;
  if ($[0] !== isGiftCard || $[1] !== isMembership || $[2] !== isPackage || $[3] !== line) {
    t1 = isGiftCard ? giftCardSubtitle(line) : isPackage ? packageSubtitle(line) : isMembership ? membershipSubtitle(line) : regularCatalogSubtitle(line);
    $[0] = isGiftCard;
    $[1] = isMembership;
    $[2] = isPackage;
    $[3] = line;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const subtitle = t1;
  let t2;
  if ($[5] !== line) {
    t2 = checkoutLineIcon(line);
    $[5] = line;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const Icon = t2;
  let t3;
  if ($[7] !== Icon || $[8] !== isGiftCard || $[9] !== isMembership || $[10] !== isPackage || $[11] !== line.snapshot.imageUrl || $[12] !== line.snapshot.name) {
    t3 = <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-md bg-background-secondary text-primary">{!isGiftCard && !isPackage && !isMembership && line.snapshot.imageUrl ? <img src={line.snapshot.imageUrl} alt={line.snapshot.name} loading="lazy" className="h-full w-full object-cover" /> : <Icon className="h-4 w-4" />}</div>;
    $[7] = Icon;
    $[8] = isGiftCard;
    $[9] = isMembership;
    $[10] = isPackage;
    $[11] = line.snapshot.imageUrl;
    $[12] = line.snapshot.name;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== line.snapshot.name) {
    t4 = <p className="truncate text-body-emphasis text-foreground">{line.snapshot.name}</p>;
    $[14] = line.snapshot.name;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = isPackage ? "line-clamp-2 whitespace-pre-line" : "truncate";
  let t6;
  if ($[16] !== t5) {
    t6 = cx("mt-0.5 text-caption text-foreground-secondary", t5);
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== subtitle || $[19] !== t6) {
    t7 = <p className={t6}>{subtitle}</p>;
    $[18] = subtitle;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== isMembership || $[22] !== line || $[23] !== onReplace) {
    t8 = !isMembership && <QuantityStepper value={line.quantity} onChange={quantity => onReplace(withLineQuantity(line, quantity))} max={quantityMaxFor(line)} className="rounded-lg shadow-none [&_button]:h-7 [&_button]:w-7 [&_input]:w-8" />;
    $[21] = isMembership;
    $[22] = line;
    $[23] = onReplace;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isMembership || $[26] !== line.snapshot.membership || $[27] !== pricing.unitPriceCents) {
    t9 = isMembership ? line.snapshot.membership?.shopBillingStart && line.snapshot.membership.shopBillingStart !== MembershipShopBillingStart.ENROLLMENT_DAY ? `First charge ${formatCurrency(pricing.unitPriceCents)}` : `Initial charge ${formatCurrency(pricing.unitPriceCents)}` : `${formatCurrency(pricing.unitPriceCents)} each`;
    $[25] = isMembership;
    $[26] = line.snapshot.membership;
    $[27] = pricing.unitPriceCents;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <span className="text-foreground-secondary">{t9}</span>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== pricing.hasPromotion || $[32] !== pricing.originalUnitPriceCents) {
    t11 = pricing.hasPromotion && <span className="text-foreground-secondary line-through">{formatCurrency(pricing.originalUnitPriceCents)}</span>;
    $[31] = pricing.hasPromotion;
    $[32] = pricing.originalUnitPriceCents;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <span className="flex flex-wrap items-baseline gap-1.5 text-caption tabular-nums">{t10}{t11}</span>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t8) {
    t13 = <div className="mt-2 flex flex-wrap items-center gap-2">{t8}{t12}</div>;
    $[37] = t12;
    $[38] = t8;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t4 || $[42] !== t7) {
    t14 = <div className="min-w-0">{t4}{t7}{t13}</div>;
    $[40] = t13;
    $[41] = t4;
    $[42] = t7;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== pricing.lineTotalCents) {
    t15 = formatCurrency(pricing.lineTotalCents);
    $[44] = pricing.lineTotalCents;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t15) {
    t16 = <p className="text-body-emphasis text-foreground">{t15}</p>;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== pricing.hasPromotion || $[49] !== pricing.originalLineTotalCents) {
    t17 = pricing.hasPromotion && <p className="text-caption text-foreground-secondary line-through">{formatCurrency(pricing.originalLineTotalCents)}</p>;
    $[48] = pricing.hasPromotion;
    $[49] = pricing.originalLineTotalCents;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t16 || $[52] !== t17) {
    t18 = <div className="flex flex-wrap items-baseline justify-end gap-2 text-right tabular-nums">{t16}{t17}</div>;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== isGiftCard || $[55] !== line.snapshot.name) {
    t19 = isGiftCard && <button type="button" onClick={() => setIsEditing(true)} aria-label={`Edit ${line.snapshot.name}`} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"><FiEdit2 className="h-3.5 w-3.5" /></button>;
    $[54] = isGiftCard;
    $[55] = line.snapshot.name;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = `Remove ${line.snapshot.name} from cart`;
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <FiTrash2 className="h-3.5 w-3.5" />;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== onRemove || $[59] !== t20) {
    t22 = <button type="button" onClick={onRemove} aria-label={t20} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-hover hover:text-status-error-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t21}</button>;
    $[58] = onRemove;
    $[59] = t20;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t19 || $[62] !== t22) {
    t23 = <div className="flex items-center gap-0.5">{t19}{t22}</div>;
    $[61] = t19;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t18 || $[65] !== t23) {
    t24 = <div className="flex flex-none flex-col items-end gap-1">{t18}{t23}</div>;
    $[64] = t18;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== t14 || $[68] !== t24) {
    t25 = <div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-4">{t14}{t24}</div></div>;
    $[67] = t14;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== isEditing || $[71] !== isGiftCard || $[72] !== line || $[73] !== onReplace) {
    t26 = isGiftCard && <GiftCardConfiguratorDialog selection={isEditing ? {
      kind: "edit",
      line
    } : null} onConfirm={updated => {
      onReplace(updated);
      setIsEditing(false);
    }} onCancel={() => setIsEditing(false)} />;
    $[70] = isEditing;
    $[71] = isGiftCard;
    $[72] = line;
    $[73] = onReplace;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t25 || $[76] !== t26 || $[77] !== t3) {
    t27 = <div className="group flex items-start gap-3">{t3}{t25}{t26}</div>;
    $[75] = t25;
    $[76] = t26;
    $[77] = t3;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  return t27;
};
export const SHIPPING_UNAVAILABLE_MESSAGE = "Shipping is no longer available for this order. Please choose pickup.";
interface PaymentStepProps {
  lines: ShopCartLine[];
  totalCents: number;
  checkoutNonce?: string;
  pickupLocationId?: string | null;
  shippingBuyer?: PurchaseBuyer | null;
  fulfillmentMethod?: ShopFulfillmentMethod | null;
  onRemoveLine: (lineId: string) => void;
  onReplaceLine: (lineId: string, line: ShopCartLine) => void;
  /** Called with the total the buyer was charged, in cents. */
  onPaymentComplete: (totalCents: number, charges: PurchaseCharges) => void;
  onChangeLocation?: () => void;
  onChangeShippingAddress?: () => void;
  onChangeFulfillmentMethod?: () => void;
  /** Pricing said this order can no longer ship; the flow sends the patient back to choose. */
  onShippingUnavailable?: (message: string) => void;
}

/**
 * Order summary + payment picker. Generic — knows about cart lines and
 * per-type renderers via `renderCartLine`, plus per-type wire serialization
 * via the adapter registry. No item-type-specific copy or logic lives here.
 */
const PaymentStep: React.FC<PaymentStepProps> = ({
  lines,
  totalCents,
  checkoutNonce,
  pickupLocationId,
  shippingBuyer,
  fulfillmentMethod,
  onRemoveLine,
  onReplaceLine,
  onPaymentComplete,
  onChangeLocation,
  onChangeShippingAddress,
  onChangeFulfillmentMethod,
  onShippingUnavailable
}) => {
  const tenant = useParams<{
    tenant?: string;
  }>()?.tenant;
  const {
    buyer,
    isAuthenticated,
    ensureAuthenticated
  } = usePurchaseAuth();
  const {
    toast
  } = useToast();
  const [serverPricing, setServerPricing] = useState<ShopPurchasePricingResponse | null>(null);
  const [isPayingWithPatientCredit, setIsPayingWithPatientCredit] = useState(false);
  const [usePatientCredit, setUsePatientCredit] = useState(true);
  const [committedPatientCreditCents, setCommittedPatientCreditCents] = useState(0);
  const [cardPaymentSelected, setCardPaymentSelected] = useState(false);
  const [cardSessionEnabled, setCardSessionEnabled] = useState(false);
  const [pricingErrorMessage, setPricingErrorMessage] = useState<string | null>(null);
  const isPayingWithPatientCreditRef = useRef(false);
  const {
    catalog
  } = useShopCatalog();
  const promotionMessages = useMemo(() => getShopPromotionMessages(catalog.promotions, lines), [catalog.promotions, lines]);
  const {
    data: frontPageData
  } = useQuery({
    ...selfScheduleQueryOptions.frontPage(),
    enabled: Boolean(pickupLocationId)
  });
  const {
    data: publicTenant
  } = useQuery(tenantQueryOptions.public(tenant));
  const pickupLocation = frontPageData?.locations?.find(location => location.id === pickupLocationId);
  const pickupLocationName = pickupLocation?.name ?? "Selected location";
  const sessionEnabled = lines.length > 0 && Boolean(buyer?.id);
  const renewalControlEnabled = publicTenant?.enabledModules?.includes("MEMBERSHIP_RENEWAL_CONTROL") === true;
  const hasScheduledMembershipStart = lines.some(line => line.itemType === ItemType.MEMBERSHIP && isMembershipBillingStartFuture(line.snapshot.membership?.shopBillingStart, publicTenant?.defaultTimezone));
  const hasRenewingMembership = lines.some(line_0 => line_0.itemType === ItemType.MEMBERSHIP && (!renewalControlEnabled || line_0.snapshot.membership?.autoRenew !== false));
  const membershipRequiresReusableCard = hasScheduledMembershipStart || hasRenewingMembership;
  const serializedItems = useMemo(() => lines.map(line_1 => {
    const adapter = getAdapter(line_1.itemType);
    return adapter ? adapter.serializeForCheckout(line_1) : null;
  }).filter((s): s is NonNullable<typeof s> => s !== null), [lines]);
  const membershipPaymentPolicy = useMemo(() => lines.filter(line_2 => line_2.itemType === ItemType.MEMBERSHIP).map(line_3 => ({
    itemId: line_3.itemId,
    autoRenew: !renewalControlEnabled || line_3.snapshot.membership?.autoRenew !== false,
    recurringPrice: line_3.snapshot.membership?.recurringPrice ?? null,
    billingFrequency: line_3.snapshot.membership?.billingFrequency ?? null,
    shopBillingStart: line_3.snapshot.membership?.shopBillingStart ?? MembershipShopBillingStart.ENROLLMENT_DAY
  })), [lines, renewalControlEnabled]);

  // Fingerprint both cart pricing and the effective membership payment policy
  // so a staff policy edit discards payment sessions with stale card rules.
  const cartFingerprint = useMemo(() => JSON.stringify({
    items: serializedItems,
    membershipPaymentPolicy
  }), [membershipPaymentPolicy, serializedItems]);
  const shippingAddressFingerprint = useMemo(() => shippingBuyer ? JSON.stringify({
    address: shippingBuyer.address ?? null,
    addressLineTwo: shippingBuyer.addressLineTwo ?? null,
    city: shippingBuyer.city ?? null,
    state: shippingBuyer.state ?? null,
    zipCode: shippingBuyer.zipCode ?? null
  }) : null, [shippingBuyer]);
  const pricingRequestKey = useMemo(() => JSON.stringify({
    buyerId: buyer?.id ?? null,
    cart: cartFingerprint,
    pickupLocationId: pickupLocationId ?? null,
    fulfillmentMethod: fulfillmentMethod ?? null,
    shippingAddress: shippingAddressFingerprint
  }), [buyer?.id, cartFingerprint, fulfillmentMethod, pickupLocationId, shippingAddressFingerprint]);
  const checkoutIdempotencyKey = useMemo(() => buildShopCheckoutIdempotencyKey({
    buyerId: buyer?.id,
    checkoutNonce,
    cartFingerprint,
    pickupLocationId,
    fulfillmentMethod,
    shippingAddressFingerprint
  }), [buyer?.id, cartFingerprint, checkoutNonce, fulfillmentMethod, pickupLocationId, shippingAddressFingerprint]);
  const currentPricingRequestKeyRef = useRef(pricingRequestKey);
  const {
    data: purchasePricing,
    error: purchasePricingError,
    refetch: refetchPurchasePricing
  } = useQuery({
    queryKey: ["shop", "purchase-pricing", pricingRequestKey],
    queryFn: async () => {
      if (!buyer) throw new Error("Buyer not authenticated");
      return await fetchPurchasePricing({
        patientId: buyer.id,
        items: serializedItems,
        pickupLocationId: pickupLocationId ?? undefined,
        fulfillmentMethod: fulfillmentMethod ?? undefined,
        checkoutIdempotencyKey
      });
    },
    enabled: sessionEnabled
  });
  useEffect(() => {
    currentPricingRequestKeyRef.current = pricingRequestKey;
    setServerPricing(null);
    setCommittedPatientCreditCents(0);
    setCardPaymentSelected(false);
    setCardSessionEnabled(false);
    setPricingErrorMessage(null);
  }, [pricingRequestKey]);
  useEffect(() => {
    if (!purchasePricing) return;
    setServerPricing(purchasePricing);
    setCommittedPatientCreditCents(purchasePricing.appliedPatientCreditCents);
    if (purchasePricing.appliedPatientCreditCents > 0) {
      setUsePatientCredit(true);
      setCardPaymentSelected(true);
    }
  }, [purchasePricing]);
  const onShippingUnavailableRef = useRef(onShippingUnavailable);
  onShippingUnavailableRef.current = onShippingUnavailable;
  /** Hands a "can no longer ship" 409 to the flow's recovery; false for any other error. */
  const recoverIfShippingUnavailable = useCallback(async (err: unknown): Promise<boolean> => {
    const recover = onShippingUnavailableRef.current;
    if (!recover) return false;
    const message = await parseError(err, "");
    if (message !== SHIPPING_UNAVAILABLE_MESSAGE) return false;
    recover(message);
    return true;
  }, []);
  useEffect(() => {
    if (!purchasePricingError) {
      setPricingErrorMessage(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      if (await recoverIfShippingUnavailable(purchasePricingError)) return;
      const message_0 = await parseError(purchasePricingError, "We couldn't refresh this cart. Check the items and try again.");
      if (!cancelled) setPricingErrorMessage(message_0);
    })();
    return () => {
      cancelled = true;
    };
  }, [purchasePricingError, recoverIfShippingUnavailable]);
  const isWaitingForServerTotal = sessionEnabled && serverPricing === null && !purchasePricingError;
  const pricingPreview = useMemo(() => getShopCartPricingPreview(lines, catalog.items, catalog.shipping, fulfillmentMethod), [catalog.items, catalog.shipping, fulfillmentMethod, lines]);
  const pricingByLineId = pricingPreview.pricingByLineId;
  const requiresShipping = fulfillmentMethod !== ShopFulfillmentMethod.PICKUP && cartCanShip(lines, catalog.items, catalog.shipping);
  const catalogDisplayTotalCents = pricingPreview.displaySubtotalCents;
  const catalogDiscountTotalCents = Math.max(0, totalCents - catalogDisplayTotalCents);
  const shippingFeeCents = serverPricing?.shippingFeeCents ?? pricingPreview.shippingFeeCents;
  const displayTotalCents = serverPricing?.total ?? catalogDisplayTotalCents + shippingFeeCents;
  const displaySubtotalCents = serverPricing?.subtotalCents ?? totalCents;
  const discountTotalCents = serverPricing?.discountTotalCents ?? catalogDiscountTotalCents;
  const taxCents = serverPricing?.taxTotalCents ?? 0;
  const purchaseCharges: PurchaseCharges = {
    taxCents,
    shippingCents: shippingFeeCents
  };
  const hasPatientCredit = serverPricing !== null && serverPricing.total > 0 && serverPricing.availablePatientCreditCents > 0;
  const membershipCreditUnavailable = hasScheduledMembershipStart || membershipRequiresReusableCard && (serverPricing?.availablePatientCreditCents ?? 0) >= displayTotalCents;
  const requiresCardStorage = hasScheduledMembershipStart || membershipRequiresReusableCard && serverPricing?.total === 0;
  const selectedPatientCreditCents = usePatientCredit && !membershipCreditUnavailable ? Math.min(serverPricing?.availablePatientCreditCents ?? 0, displayTotalCents) : 0;
  const appliedPatientCreditCents = committedPatientCreditCents || selectedPatientCreditCents;
  const cardAmountDueCents = Math.max(0, displayTotalCents - appliedPatientCreditCents);
  const patientCreditAmountForCard = cardPaymentSelected ? appliedPatientCreditCents : 0;
  // A Rainforest payment-method config is single-use. Include the checkout key
  // so buying the same cart again cannot reuse React Query's cached config.
  const paymentSessionInputKey = JSON.stringify({
    pricingRequestKey,
    checkoutIdempotencyKey,
    patientCreditAmountCents: patientCreditAmountForCard
  });
  // Only the credit-only request needs the cart held still. Split checkouts
  // redeem nothing until the card settles, so editing mid-flow just re-prices.
  const isCartLocked = isPayingWithPatientCredit;
  const promotionProgressMessages = promotionMessages.filter(message_1 => message_1.status === "progress" && !serverPricing?.appliedPromotions.some(promotion => promotion.id === message_1.promotionId));
  const handlePayWithPatientCredit = async () => {
    if (!buyer || isPayingWithPatientCreditRef.current) return;
    isPayingWithPatientCreditRef.current = true;
    setIsPayingWithPatientCredit(true);
    try {
      const result = await completePurchaseWithPatientCredit({
        patientId: buyer.id,
        items: serializedItems,
        pickupLocationId: pickupLocationId ?? undefined,
        fulfillmentMethod: fulfillmentMethod ?? undefined,
        checkoutIdempotencyKey
      });
      if (!result?.completed) throw new Error("Patient credit payment failed");
      onPaymentComplete(displayTotalCents, purchaseCharges);
    } catch (err_0) {
      if (await recoverIfShippingUnavailable(err_0)) return;
      // Refresh the balance but keep the last known pricing on screen. Clearing
      // it hides the credit option behind the loading state, and a refetch that
      // returns identical pricing is deduped to the same object — so nothing
      // would arrive to restore it and checkout would stall on "Calculating…".
      void refetchPurchasePricing();
      toast({
        title: "Patient credit payment failed",
        description: await parseError(err_0, err_0 instanceof Error ? err_0.message : "Your credit could not be applied. Please refresh your balance and try again."),
        variant: "error"
      });
    } finally {
      isPayingWithPatientCreditRef.current = false;
      setIsPayingWithPatientCredit(false);
    }
  };
  const handlePayWithSavedMethod = async (paymentMethodId: string) => {
    if (!buyer) throw new Error("Buyer not authenticated");
    try {
      const result_0 = await chargePurchaseWithSavedMethod({
        patientId: buyer.id,
        items: serializedItems,
        pickupLocationId: pickupLocationId ?? undefined,
        fulfillmentMethod: fulfillmentMethod ?? undefined,
        checkoutIdempotencyKey,
        patientCreditAmountCents: patientCreditAmountForCard || undefined,
        isSurcharged: false,
        paymentMethodId
      });
      if (!result_0) throw new Error("Charge failed");
      // An accepted payment completes the shop UI, including PROCESSING.
      // Settlement and membership activation continue server-side.
      return true;
    } catch (err_1) {
      if (await recoverIfShippingUnavailable(err_1)) throw err_1;
      toast({
        title: "Payment failed",
        description: await parseError(err_1, err_1 instanceof Error ? err_1.message : "There was an issue processing your payment. Please try again."),
        variant: "error"
      });
      throw err_1;
    }
  };
  return <div className="space-y-10">
      <section>
        <header>
          <p className="text-overline text-foreground-secondary">
            Pay securely
          </p>
          <p className="mt-2 font-serif text-[2.75rem] font-medium leading-none tracking-tight text-foreground tabular-nums">
            {purchasePricingError ? "Unavailable" : isWaitingForServerTotal ? "Calculating…" : formatCurrency(displayTotalCents)}
          </p>
          <p className="mt-3 text-caption text-foreground-secondary">
            {lines.length} item<span>{lines.length === 1 ? "" : "s"}</span>{" "}
            <span>in your order</span>
          </p>
        </header>

        <div className="mt-8 divide-y divide-border border-y border-border">
          {lines.map(line_4 => <fieldset key={line_4.lineId} disabled={isCartLocked} className="min-w-0 py-4 disabled:cursor-not-allowed disabled:opacity-60">
              <CheckoutLineItem line={line_4} onReplace={updated => onReplaceLine(line_4.lineId, updated)} onRemove={() => onRemoveLine(line_4.lineId)} pricing={pricingByLineId.get(line_4.lineId)!} />
            </fieldset>)}
        </div>

        {fulfillmentMethod && <div className="flex items-start justify-between gap-4 border-b border-border py-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-none items-center justify-center text-primary">
                {fulfillmentMethod === ShopFulfillmentMethod.PICKUP ? <FaStore className="h-4 w-4" /> : <FaTruck className="h-4 w-4" />}
              </div>
              <div>
                <p className="text-caption uppercase tracking-wide text-foreground-secondary">
                  Delivery
                </p>
                <p className="text-body-emphasis text-foreground">
                  {fulfillmentMethod === ShopFulfillmentMethod.PICKUP ? "Pick up at the clinic" : "Ship to me"}
                </p>
              </div>
            </div>
            {onChangeFulfillmentMethod && <Button variant="ghost" size="sm" onClick={onChangeFulfillmentMethod} disabled={isCartLocked} className="cursor-pointer text-primary">
                <span>Change</span>
              </Button>}
          </div>}

        {pickupLocationId && <div className="flex items-start justify-between gap-4 border-b border-border py-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-none items-center justify-center text-primary">
                <FaMapMarkerAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="text-caption uppercase tracking-wide text-foreground-secondary">
                  Pickup At
                </p>
                <p className="text-body-emphasis text-foreground">
                  {pickupLocationName}
                </p>
              </div>
            </div>
            {onChangeLocation && <Button variant="ghost" size="sm" onClick={onChangeLocation} disabled={isCartLocked} className="cursor-pointer text-primary">
                <span>Change</span>
              </Button>}
          </div>}

        {shippingBuyer?.address && <div className="flex items-start justify-between gap-4 border-b border-border py-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-none items-center justify-center text-primary">
                <FaMapMarkerAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="text-caption uppercase tracking-wide text-foreground-secondary">
                  Ship to
                </p>
                <p className="text-body-emphasis text-foreground">
                  {shippingBuyer.address}
                </p>
                {shippingBuyer.addressLineTwo && <p className="text-caption text-foreground-secondary">
                    {shippingBuyer.addressLineTwo}
                  </p>}
                <p className="text-caption text-foreground-secondary">
                  {[shippingBuyer.city, shippingBuyer.state].filter(Boolean).join(", ")}{" "}
                  {shippingBuyer.zipCode}
                </p>
              </div>
            </div>
            {onChangeShippingAddress && <Button variant="ghost" size="sm" onClick={onChangeShippingAddress} disabled={isCartLocked} className="cursor-pointer text-primary">
                <span>Change</span>
              </Button>}
          </div>}

        <PromotionProgress messages={promotionProgressMessages} className="mt-5" />

        <dl className="mt-6 space-y-3 text-body">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-secondary">Subtotal</dt>
            <dd className="font-medium text-foreground tabular-nums">
              {formatCurrency(displaySubtotalCents)}
            </dd>
          </div>
          {discountTotalCents > 0 && <>
              <div className="flex items-center justify-between gap-4 text-status-success-text">
                <dt>Promotions</dt>
                <dd className="font-medium tabular-nums">
                  -{formatCurrency(discountTotalCents)}
                </dd>
              </div>
              {serverPricing?.appliedPromotions.map(promotion_0 => <div key={promotion_0.id} className="flex items-start justify-between gap-4 pl-3 text-caption text-foreground-secondary">
                  <dt>{promotion_0.name}</dt>
                  <dd className="flex-none tabular-nums">
                    -{formatCurrency(promotion_0.discountCents)}
                  </dd>
                </div>)}
            </>}
          {(requiresShipping || (serverPricing?.shippingFeeCents ?? 0) > 0) && <div className="flex items-center justify-between gap-4">
              <dt className="text-foreground-secondary">Shipping</dt>
              <dd className="font-medium text-foreground tabular-nums">
                {isWaitingForServerTotal ? "Calculating…" : shippingFeeCents > 0 ? formatCurrency(shippingFeeCents) : "Free"}
              </dd>
            </div>}
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-secondary">Tax</dt>
            <dd className="text-right text-caption text-foreground-secondary tabular-nums">
              {isWaitingForServerTotal ? "Calculating…" : formatCurrency(taxCents)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
            <dt className="font-medium text-foreground">Total due</dt>
            <dd className="font-semibold text-foreground tabular-nums">
              {isWaitingForServerTotal ? "Calculating…" : formatCurrency(displayTotalCents)}
            </dd>
          </div>
          {appliedPatientCreditCents > 0 && <>
              <div className="flex items-center justify-between gap-4 text-status-success-text">
                <dt>Patient credit</dt>
                <dd className="font-medium tabular-nums">
                  -{formatCurrency(appliedPatientCreditCents)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="font-medium text-foreground">
                  {cardAmountDueCents > 0 ? "Card due" : "Remaining due"}
                </dt>
                <dd className="font-semibold text-foreground tabular-nums">
                  {formatCurrency(cardAmountDueCents)}
                </dd>
              </div>
            </>}
        </dl>
      </section>

      {isAuthenticated && !membershipCreditUnavailable && !cardPaymentSelected && !purchasePricingError && <PatientCreditPaymentOption balanceCents={serverPricing?.availablePatientCreditCents ?? 0} totalCents={displayTotalCents} selected={usePatientCredit} disabled={cardSessionEnabled} isPricingLoading={isWaitingForServerTotal} isSubmitting={isPayingWithPatientCredit} onSelectedChange={setUsePatientCredit} onPay={handlePayWithPatientCredit} />}

      <section>
        <div className="mb-5">
          <h2 className="text-h3 text-foreground">
            {requiresCardStorage ? hasScheduledMembershipStart ? "Add a card for your membership" : "Add a card for renewals" : appliedPatientCreditCents > 0 && cardAmountDueCents > 0 ? "Pay the rest with card" : "Pay with card"}
          </h2>
          <p className="mt-2 text-caption leading-relaxed text-foreground-secondary">
            {hasScheduledMembershipStart ? hasRenewingMembership ? "A reusable card is required for your scheduled first payment and future renewals." : "A reusable card is required for your scheduled membership payment." : membershipRequiresReusableCard ? "A reusable card is required for membership renewals." : appliedPatientCreditCents > 0 && cardAmountDueCents > 0 ? `${formatCurrency(appliedPatientCreditCents)} in patient credit will be applied first.` : "Choose a saved method or add a new one to finish securely."}
          </p>
        </div>

        {isAuthenticated ? <RainforestPayinSessionProvider mutationOptions={{
        mutationFn: async ({
          isSurcharged
        }: {
          isSurcharged: boolean;
        }) => {
          if (!buyer) {
            throw new Error("Buyer not authenticated");
          }
          const submittedPricingRequestKey = pricingRequestKey;
          const response = await shopMutationOptions.createPayinConfig().mutationFn({
            patientId: buyer.id,
            items: serializedItems,
            pickupLocationId: pickupLocationId ?? undefined,
            fulfillmentMethod: fulfillmentMethod ?? undefined,
            checkoutIdempotencyKey,
            patientCreditAmountCents: patientCreditAmountForCard || undefined,
            isSurcharged
          }).catch(async (err_2: unknown) => {
            await recoverIfShippingUnavailable(err_2);
            throw err_2;
          });
          if (currentPricingRequestKeyRef.current === submittedPricingRequestKey) {
            setServerPricing(response);
            setCommittedPatientCreditCents(response?.appliedPatientCreditCents ?? 0);
          }
          return response;
        }
      }} buildInput={({
        isSurcharged: isSurcharged_0
      }) => ({
        isSurcharged: isSurcharged_0
      })}
      // `cardSessionEnabled` only flips once the picker is on screen, and
      // the picker only renders once the card path is the chosen one, so
      // it already implies the buyer committed to paying by card.
      enabled={sessionEnabled && !requiresCardStorage && (serverPricing?.total === 0 || cardSessionEnabled)} patient={buyer} inputKey={paymentSessionInputKey}>
            {purchasePricingError ? <div className="space-y-3 rounded-lg border border-status-error-border bg-status-error-background p-4">
                <p className="text-caption text-status-error-text">
                  {pricingErrorMessage ?? "Unable to price this cart."}
                </p>
                <Button variant="primary" size="sm" onClick={() => void refetchPurchasePricing()} className="cursor-pointer">
                  Try again
                </Button>
              </div> : serverPricing === null ? <p className="text-caption text-foreground-secondary">
                Loading secure payment options…
              </p> : hasPatientCredit && !membershipCreditUnavailable && !cardPaymentSelected ? <Button variant="primary" onClick={() => {
          if (cardAmountDueCents === 0) setUsePatientCredit(false);
          setCardPaymentSelected(true);
        }} disabled={isPayingWithPatientCredit} className="w-full cursor-pointer">
                {cardAmountDueCents === 0 ? "Pay with card instead" : `Continue to pay ${formatCurrency(cardAmountDueCents)} with card`}
              </Button> : <PaymentMethodPicker patientId={buyer?.id} amountCents={cardAmountDueCents} allowAch={false} cardOnly={membershipRequiresReusableCard} isAmountLoading={isWaitingForServerTotal} payWithSavedMethod={handlePayWithSavedMethod} onComplete={() => onPaymentComplete(displayTotalCents, purchaseCharges)} onNewCardSessionRequested={() => setCardSessionEnabled(true)} saveCardOnly={requiresCardStorage} paymentMethodConfigKey={paymentSessionInputKey} createPaymentMethodConfig={async () => {
          if (!buyer) return null;
          return await createMembershipPaymentMethodConfig({
            patientId: buyer.id,
            items: serializedItems,
            pickupLocationId: pickupLocationId ?? undefined,
            fulfillmentMethod: fulfillmentMethod ?? undefined,
            checkoutIdempotencyKey,
            isSurcharged: false
          });
        }} savePaymentMethod={async (rainforestPaymentMethodId, paymentMethodConfigId) => {
          if (!buyer) throw new Error("Buyer not authenticated");
          const paymentMethod = await saveMembershipPaymentMethod({
            patientId: buyer.id,
            items: serializedItems,
            pickupLocationId: pickupLocationId ?? undefined,
            fulfillmentMethod: fulfillmentMethod ?? undefined,
            checkoutIdempotencyKey,
            isSurcharged: false,
            rainforestPaymentMethodId,
            paymentMethodConfigId
          });
          if (!paymentMethod) throw new Error("Failed to save payment method");
          return paymentMethod.id;
        }} />}
          </RainforestPayinSessionProvider> : <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <FaLock className="mt-1 h-4 w-4 flex-none text-primary" />
              <div>
                <p className="text-body-strong text-foreground">
                  Sign in to pay
                </p>
                <p className="mt-1 text-caption text-foreground-secondary">
                  Use your phone or patient portal to verify it&apos;s you.
                </p>
              </div>
            </div>
            <Button variant="primary" onClick={() => ensureAuthenticated()} className="cursor-pointer">
              <span>Sign in</span>
            </Button>
          </div>}
      </section>
    </div>;
};
export default PaymentStep;